import { useEffect, useRef, useState } from 'react';
import { ImageIcon } from 'lucide-react';
type Props = {
  src: string;
  alt?: string;
  title?: string;
  className?: string;
  loading?: 'lazy' | 'eager';
  /** Packshots tonen we heel op een rustige ondergrond; sfeerbeelden vullen het vlak. */
  fit?: 'contain' | 'cover';
  /**
   * Weergavebreedte in px voor kleine plaatjes (lijsten, duimnagels). Dan vragen we een
   * verkleinde versie op (2× voor scherpe schermen) in plaats van de volle foto; lukt dat
   * niet, dan alsnog het origineel.
   */
  width?: number;
  /**
   * Weergavehoogte in px als het plaatje niet vierkant is (bijv. 48 × 56). Leeg = gelijk aan `width`.
   * Samen met `width` reserveert de browser de plek (geen verspringing).
   */
  height?: number;
  /**
   * Alleen de afmetingen voor de browser (geen verkleinde versie opvragen), voor grote foto's zoals
   * de hoofdfoto op de productpagina: zo meldt Lighthouse geen ontbrekende breedte en hoogte.
   */
  intrinsic?: {
    width: number;
    height: number;
  };
  /** Uitsnede bij `fit="cover"`: CSS object-position, bijv. "30% 60%" (core/rules/media.ts). */
  position?: string;
};

/**
 * Eén plek voor elke productfoto op de site en in het beheer.
 *
 * De huidige webshop blokkeert foto's die vanaf een ander domein worden opgevraagd
 * (hotlink-beveiliging kijkt naar de "referer"). Daarom:
 *  1. eerst direct laden zónder referer;
 *  2. lukt dat niet, dan via een beeldproxy (die zet .avif ook om naar webp);
 *  3. lukt ook dat niet, dan een rustige lege plek in plaats van een kapot icoon.
 * Zodra de foto's in de eigen mediabibliotheek staan, is stap 2 niet meer nodig.
 */
export function ProductImage({
  src,
  alt = '',
  title,
  className = '',
  loading = 'lazy',
  fit = 'contain',
  width,
  height,
  intrinsic,
  position
}: Props) {
  const [stage, setStage] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);
  useEffect(() => {
    setStage(0);
    setLoaded(false);
  }, [src]);

  // Uit de cache geladen vóór React meeluisterde? Dan is er geen load-event meer.
  useEffect(() => {
    if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) setLoaded(true);
  }, [src, stage]);

  // Lokaal (upload of eigen domein): de proxy kan daar niet bij, dus alleen het origineel.
  const local = !src || src.startsWith('blob:') || src.startsWith('data:') || src.startsWith('/') && !src.startsWith('//');
  const base = fit === 'contain' ? 'bg-shell object-contain' : 'object-cover';
  // Klein plaatje van elders: eerst verkleind via de proxy, dan het origineel. Anders: eerst het origineel, dan de proxy.
  const small = width !== undefined && !local;
  const sources = local ? [src] : small ? [proxied(src, width * 2), src] : [src, proxied(src)];
  if (!src || stage >= sources.length) {
    return <span role={alt ? 'img' : undefined} aria-label={alt || undefined} title={title} className={`flex items-center justify-center bg-shell text-muted/40 ${className}`} data-id="element-9241" data-mp="3v20jbvgfri28">
        <ImageIcon className="h-5 w-5" strokeWidth={1.25} data-id="element-9242" data-mp="3gwqhie92qw86" />
      </span>;
  }
  return <img ref={imgRef} src={sources[stage]} alt={alt} title={title} loading={loading} width={intrinsic?.width ?? width} height={intrinsic?.height ?? height ?? width} decoding="async" referrerPolicy="no-referrer" onLoad={() => setLoaded(true)} onError={() => setStage(s => s + 1)} style={position ? {
    objectPosition: position
  } : undefined}
  // Tot de foto er is: zacht pulserend vlak op dezelfde plek (geen verspringing).
  className={`${base} ${loaded ? '' : 'motion-safe:animate-pulse'} ${className}`} data-id="element-9243" data-mp="3kvqxzq42xms0" />;
}
const proxied = (src: string, w = 900) => `https://images.weserv.nl/?url=${encodeURIComponent(src.replace(/^https?:\/\//, ''))}&w=${w}&output=webp&q=85`;